import React, { useState, useMemo, useRef, useEffect } from "react";
import { FaChevronDown, FaUser, FaSignOutAlt, FaSearch, FaBuilding, FaTimes, FaCheck, FaCog, FaRegComments, FaPhoneAlt } from "react-icons/fa";
import { useUserProfile } from "../hooks/useUserProfile";
import { useBuilding } from "../context/BuildingContext";
import { useAdminBadges } from "../hooks/useAdminBadges";
import GlobalSearch from "./GlobalSearch";
import TutorialButton from "./TutorialButton";

import RoleSwitcher from "./RoleSwitcher";

const HeaderAdmin = ({ selected, setSelected, userType, onLogout }) => {
  const { photoURL, initial } = useUserProfile();
  const [showMenu, setShowMenu] = useState(false);
  const [showBuildingDropdown, setShowBuildingDropdown] = useState(false);
  const [buildingSearchQuery, setBuildingSearchQuery] = useState("");
  
  const { buildings, selectedBuilding, setSelectedBuilding } = useBuilding();
  // Avisos de consultas/llamadas para el chip del header (mismos números que el sidebar).
  const { pendingConsultas, proximaLlamada } = useAdminBadges(selectedBuilding?.buildingid);
  const buildingSearchRef = useRef(null);

  // Focus en el input de búsqueda cuando se abre el dropdown
  useEffect(() => {
    if (showBuildingDropdown && buildingSearchRef.current) {
      buildingSearchRef.current.focus();
    }
  }, [showBuildingDropdown]);

  // Filtrar edificios según búsqueda
  const filteredBuildings = useMemo(() => {
    if (!buildings) return [];
    if (!buildingSearchQuery.trim()) return buildings;
    
    const query = buildingSearchQuery.toLowerCase();
    return buildings.filter(b => 
      (b.name && b.name.toLowerCase().includes(query)) ||
      (b.address && b.address.toLowerCase().includes(query)) ||
      (b.city && b.city.toLowerCase().includes(query))
    );
  }, [buildings, buildingSearchQuery]);

  const handleBuildingSelect = (building) => {
    setSelectedBuilding(building);
    setShowBuildingDropdown(false);
    setBuildingSearchQuery("");
  };

  // Pantallas donde no se permite deseleccionar edificio (requieren uno seleccionado)
  const requiresBuilding = selected === "residentes";
  // Pantallas donde el selector de edificio se bloquea completamente (sub-pantallas de unidad y detalle de edificio)
  const lockBuilding = ["unidad", "pagosunidad", "incidenciasunidad", "editaredificio"].includes(selected);

  const handleClearFilter = () => {
    if (requiresBuilding) return;
    setSelectedBuilding(null);
    setShowBuildingDropdown(false);
    setBuildingSearchQuery("");
  };

  const handleCloseDropdowns = () => {
    setShowBuildingDropdown(false);
    setShowMenu(false);
    setBuildingSearchQuery("");
  };

  return (
    <header style={styles.header}>
      {/* Izquierda - Búsqueda Global */}
      <div style={styles.left}>
        <GlobalSearch setSelected={setSelected} />
      </div>

      {/* Centro - Filtro de Edificio con Buscador */}
      {/* Presupuestos y Reglamento: pantallas con su propio contexto, no mostrar selector */}
      {selected !== "edificios" && selected !== "presupuestos" && selected !== "reglamento" && <div style={styles.center}>
        <div style={styles.buildingFilterContainer} data-tutorial="header-building-selector">
          <button
            style={{
              ...styles.buildingButton,
              borderColor: selectedBuilding ? "#0080ff" : "#e5e7eb",
              backgroundColor: selectedBuilding ? "#f0f7ff" : "#ffffff",
              ...(lockBuilding ? { opacity: 0.7, cursor: "default" } : {}),
            }}
            onClick={() => !lockBuilding && setShowBuildingDropdown(!showBuildingDropdown)}
            title={
              lockBuilding
                ? selected === "editaredificio"
                  ? "Salí del edificio para cambiar el filtro"
                  : "Volvé a Unidades para cambiar de edificio"
                : ""
            }
          >
            <FaBuilding size={14} color="#0080ff" />
            <span style={styles.buildingButtonText}>
              {selectedBuilding
                ? (selectedBuilding.name || `Edificio ${selectedBuilding.buildingid}`)
                : "Todos los edificios"
              }
            </span>
            {!lockBuilding && (
              <FaChevronDown
                size={10}
                color="#6b7280"
                style={{
                  transform: showBuildingDropdown ? "rotate(180deg)" : "rotate(0)",
                  transition: "transform 0.2s ease",
                }}
              />
            )}
          </button>

          {selectedBuilding && !requiresBuilding && !lockBuilding && (
            <button
              style={styles.clearFilterBtn}
              onClick={handleClearFilter}
              title="Quitar filtro"
            >
              <FaTimes size={10} />
            </button>
          )}

          {/* Dropdown con buscador */}
          {showBuildingDropdown && (
            <div style={styles.buildingDropdown}>
              {/* Buscador dentro del dropdown */}
              <div style={styles.dropdownSearchContainer}>
                <FaSearch size={12} color="#9ca3af" />
                <input
                  ref={buildingSearchRef}
                  type="text"
                  placeholder="Buscar edificio..."
                  value={buildingSearchQuery}
                  onChange={(e) => setBuildingSearchQuery(e.target.value)}
                  style={styles.dropdownSearchInput}
                />
                {buildingSearchQuery && (
                  <button
                    style={styles.dropdownClearBtn}
                    onClick={() => setBuildingSearchQuery("")}
                  >
                    <FaTimes size={8} />
                  </button>
                )}
              </div>

              {/* Lista de edificios */}
              <div style={styles.buildingList}>
                {/* Opción: Todos los edificios (oculta en pantallas que requieren edificio) */}
                {!buildingSearchQuery && !requiresBuilding && (
                  <div
                    style={{
                      ...styles.buildingOption,
                      backgroundColor: !selectedBuilding ? "#f0f7ff" : "transparent",
                    }}
                    onClick={handleClearFilter}
                    onMouseEnter={(e) => {
                      if (selectedBuilding) e.currentTarget.style.backgroundColor = "#f8f9fa";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = !selectedBuilding ? "#f0f7ff" : "transparent";
                    }}
                  >
                    <div style={styles.optionIcon}>
                      <FaBuilding size={14} color="#6b7280" />
                    </div>
                    <span style={styles.optionText}>Todos los edificios</span>
                    {!selectedBuilding && (
                      <FaCheck size={12} color="#0080ff" />
                    )}
                  </div>
                )}

                {/* Separador */}
                {!buildingSearchQuery && !requiresBuilding && filteredBuildings.length > 0 && (
                  <div style={styles.separator} />
                )}

                {/* Edificios filtrados */}
                {filteredBuildings.length > 0 ? (
                  filteredBuildings.map((building) => (
                    <div
                      key={building.id}
                      style={{
                        ...styles.buildingOption,
                        backgroundColor: selectedBuilding?.id === building.id ? "#f0f7ff" : "transparent",
                      }}
                      onClick={() => handleBuildingSelect(building)}
                      onMouseEnter={(e) => {
                        if (selectedBuilding?.id !== building.id) {
                          e.currentTarget.style.backgroundColor = "#f8f9fa";
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = selectedBuilding?.id === building.id ? "#f0f7ff" : "transparent";
                      }}
                    >
                      <div style={{
                        ...styles.optionIcon,
                        backgroundColor: "#e8f4ff",
                      }}>
                        <FaBuilding size={12} color="#0080ff" />
                      </div>
                      <div style={styles.buildingInfo}>
                        <span style={styles.buildingName}>
                          {building.name || `Edificio ${building.buildingid}`}
                        </span>
                        {building.address && (
                          <span style={styles.buildingAddress}>{building.address}</span>
                        )}
                      </div>
                      {selectedBuilding?.id === building.id && (
                        <FaCheck size={12} color="#0080ff" />
                      )}
                    </div>
                  ))
                ) : buildingSearchQuery ? (
                  <div style={styles.noResults}>
                    <FaSearch size={16} color="#d1d5db" />
                    <span>No se encontraron edificios</span>
                  </div>
                ) : null}
              </div>

              {/* Footer con contador */}
              {buildings && buildings.length > 0 && (
                <div style={styles.dropdownFooter}>
                  <span>{buildings.length} edificio{buildings.length !== 1 ? 's' : ''} disponible{buildings.length !== 1 ? 's' : ''}</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>}

      {/* Avisos de consultas y agenda: visibles siempre, sin abrir el sidebar */}
      {(pendingConsultas > 0 || proximaLlamada) && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginRight: 8 }}>
          {pendingConsultas > 0 && (
            <button
              type="button"
              onClick={() => setSelected("consultasadmin")}
              title="Consultas de residentes sin responder"
              className="habitia-transition"
              style={styles.avisoConsultas}
            >
              <FaRegComments size={12} />
              {pendingConsultas} consulta{pendingConsultas === 1 ? "" : "s"}
            </button>
          )}
          {proximaLlamada && (
            <button
              type="button"
              onClick={() => setSelected("consultasadmin")}
              title={`Llamada agendada con ${proximaLlamada.nombre}`}
              className="habitia-transition"
              style={styles.avisoLlamada}
            >
              <FaPhoneAlt size={11} />
              {(() => {
                const hoy = new Date();
                const iso = `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, "0")}-${String(hoy.getDate()).padStart(2, "0")}`;
                const dia = proximaLlamada.date === iso
                  ? "Hoy"
                  : new Date(`${proximaLlamada.date}T00:00:00`).toLocaleDateString("es-UY", { weekday: "short", day: "numeric" });
                return `${dia} ${proximaLlamada.time} · ${proximaLlamada.nombre}`;
              })()}
            </button>
          )}
        </div>
      )}

      {/* Derecha - Ayuda + Usuario */}
      <div style={styles.right}>
        <TutorialButton screenId={selected} variant="header" />
        <div
          style={styles.userContainer}
          data-tutorial="header-user-menu"
          onClick={() => setShowMenu(!showMenu)}
        >
          {photoURL ? (
            <img src={photoURL} alt="Usuario" style={styles.avatar} />
          ) : (
            <div style={styles.avatarLetter}>{initial}</div>
          )}
          <FaChevronDown size={10} color="#6b7280" />
          
          {showMenu && (
            <div style={styles.userDropdown}>
              <div 
                style={styles.userDropdownItem}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#f8f9fa"}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}
                onClick={() => { setSelected("perfil"); setShowMenu(false); }}
              >
                <FaUser size={13} color="#6b7280" />
                <span>Mi Perfil</span>
              </div>
              <div 
                style={styles.userDropdownItem}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#f8f9fa"}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}
                onClick={() => { setSelected("configuracion"); setShowMenu(false); }}
              >
                <FaCog size={13} color="#6b7280" />
                <span>Configuración</span>
              </div>
              <RoleSwitcher variant="menu" />
              <div style={styles.userDropdownDivider} />
              <div 
                style={{...styles.userDropdownItem, color: "#ef4444"}}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#fef2f2"}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}
                onClick={() => onLogout?.()}
              >
                <FaSignOutAlt size={13} color="#ef4444" />
                <span>Cerrar sesión</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Overlay */}
      {(showBuildingDropdown || showMenu) && (
        <div style={styles.overlay} onClick={handleCloseDropdowns} />
      )}
    </header>
  );
};

const styles = {
  avisoConsultas: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "6px 12px",
    borderRadius: 9999,
    border: "1px solid #4da6ff",
    backgroundColor: "#e8f4ff",
    color: "#0080ff",
    fontSize: 12,
    fontWeight: 400,
    cursor: "pointer",
    whiteSpace: "nowrap",
    fontFamily: "inherit",
  },
  avisoLlamada: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "6px 12px",
    borderRadius: 9999,
    border: "1px solid #fcd34d",
    backgroundColor: "#fef3c7",
    color: "#92400e",
    fontSize: 12,
    fontWeight: 400,
    cursor: "pointer",
    whiteSpace: "nowrap",
    fontFamily: "inherit",
  },
  header: {
    height: 56,
    backgroundColor: "#ffffff",
    borderBottom: "1px solid #e5e7eb",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0 24px",
    fontFamily: "'FT Habit', -apple-system, BlinkMacSystemFont, sans-serif",
    position: "relative",
    zIndex: 100,
  },
  
  // Búsqueda Global
  left: {
    flex: 1,
    display: "flex",
    alignItems: "center",
  },
  searchContainer: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    backgroundColor: "#f8f9fa",
    borderRadius: 8,
    padding: "8px 12px",
    width: 220,
    border: "1px solid transparent",
    transition: "all 0.2s ease",
  },
  searchInput: {
    flex: 1,
    border: "none",
    background: "transparent",
    fontSize: 13,
    color: "#1a1a1a",
    outline: "none",
    fontFamily: "'FT Habit', -apple-system, BlinkMacSystemFont, sans-serif",
  },
  clearBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 16,
    height: 16,
    backgroundColor: "#e5e7eb",
    border: "none",
    borderRadius: "50%",
    cursor: "pointer",
    color: "#6b7280",
  },

  // Filtro de Edificio
  center: {
    flex: 1,
    display: "flex",
    justifyContent: "center",
  },
  buildingFilterContainer: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    position: "relative",
  },
  buildingButton: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 14px",
    backgroundColor: "#ffffff",
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    cursor: "pointer",
    transition: "all 0.2s ease",
    fontFamily: "'FT Habit', -apple-system, BlinkMacSystemFont, sans-serif",
  },
  buildingButtonText: {
    fontSize: 13,
    fontWeight: 400,
    color: "#1a1a1a",
    maxWidth: 160,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  clearFilterBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 24,
    height: 24,
    backgroundColor: "#e5e7eb",
    border: "none",
    borderRadius: "50%",
    cursor: "pointer",
    color: "#6b7280",
    transition: "all 0.15s ease",
  },

  // Dropdown de Edificios
  buildingDropdown: {
    position: "absolute",
    top: "calc(100% + 8px)",
    left: "50%",
    transform: "translateX(-50%)",
    backgroundColor: "#ffffff",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    boxShadow: "0 16px 48px rgba(0, 0, 0, 0.12)",
    width: 320,
    zIndex: 200,
    overflow: "hidden",
  },
  dropdownSearchContainer: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "12px 14px",
    borderBottom: "1px solid #f3f4f6",
    backgroundColor: "#fafafa",
  },
  dropdownSearchInput: {
    flex: 1,
    border: "none",
    background: "transparent",
    fontSize: 13,
    color: "#1a1a1a",
    outline: "none",
    fontFamily: "'FT Habit', -apple-system, BlinkMacSystemFont, sans-serif",
  },
  dropdownClearBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 16,
    height: 16,
    backgroundColor: "#e5e7eb",
    border: "none",
    borderRadius: "50%",
    cursor: "pointer",
    color: "#6b7280",
  },
  buildingList: {
    maxHeight: 280,
    overflowY: "auto",
  },
  buildingOption: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 14px",
    cursor: "pointer",
    transition: "background-color 0.15s ease",
  },
  optionIcon: {
    width: 28,
    height: 28,
    borderRadius: 6,
    backgroundColor: "#f3f4f6",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  optionText: {
    flex: 1,
    fontSize: 13,
    fontWeight: 400,
    color: "#374151",
  },
  buildingInfo: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: 1,
    minWidth: 0,
  },
  buildingName: {
    fontSize: 13,
    fontWeight: 400,
    color: "#1a1a1a",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  buildingAddress: {
    fontSize: 11,
    color: "#9ca3af",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  separator: {
    height: 1,
    backgroundColor: "#f3f4f6",
    margin: "4px 14px",
  },
  noResults: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    padding: "24px 14px",
    color: "#9ca3af",
    fontSize: 13,
  },
  dropdownFooter: {
    padding: "10px 14px",
    borderTop: "1px solid #f3f4f6",
    backgroundColor: "#fafafa",
    fontSize: 11,
    color: "#9ca3af",
    textAlign: "center",
  },

  // Usuario
  right: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 12,
  },
  userContainer: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    cursor: "pointer",
    padding: "4px 8px",
    borderRadius: 8,
    position: "relative",
    transition: "background-color 0.15s ease",
  },
  avatar: {
    width: 32,
    height: 32,
    borderRadius: "50%",
    objectFit: "cover",
    border: "2px solid #f3f4f6",
  },
  avatarLetter: {
    width: 32,
    height: 32,
    borderRadius: "50%",
    background: "#0080ff",
    color: "white",
    fontSize: 14,
    fontWeight: 400,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  userDropdown: {
    position: "absolute",
    top: "calc(100% + 8px)",
    right: 0,
    backgroundColor: "#ffffff",
    border: "1px solid #e5e7eb",
    borderRadius: 10,
    boxShadow: "0 12px 32px rgba(0, 0, 0, 0.12)",
    minWidth: 160,
    zIndex: 200,
    overflow: "hidden",
  },
  userDropdownItem: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 14px",
    fontSize: 13,
    color: "#374151",
    cursor: "pointer",
    transition: "background-color 0.15s ease",
  },
  userDropdownDivider: {
    height: 1,
    backgroundColor: "#f3f4f6",
  },

  // Overlay
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 150,
  },
};

export default HeaderAdmin;
